import { useEffect, useRef, useState } from "react";
import { useT } from "@/i18n";

// His quantity entry, shared by the kit and the article stock cards (Z Production
// `buildQtyControls` and `openConfirm`).

// His `buildQtyControls`: in add mode the current figure, "+ qty" and Add (Enter or leaving the field
// adds; a negative removes); in correct mode − / the total / +.
export function QtyControls({ mode, qty, label, onDelta, onSet }: {
  mode: "add" | "set"; qty: number; label: string; onDelta: (n: number, plusMinus?: boolean) => void; onSet: (n: number) => void;
}) {
  const { t } = useT();
  const [val, setVal] = useState("");
  const [total, setTotal] = useState(String(qty));
  useEffect(() => setTotal(String(qty)), [qty]);
  const add = () => { const n = Math.round(Number(val.replace(",", "."))); setVal(""); if (n && Number.isFinite(n)) onDelta(n); };
  if (mode === "add") {
    return (
      <span className="ml-auto flex items-center gap-1.5">
        <span className="w-10 text-right font-mono text-base font-semibold tabular-nums text-slate-900" title={t("prod.entry.now")}>{qty}</span>
        <input value={val} onChange={(e) => setVal(e.target.value)} inputMode="numeric" placeholder={t("prod.entry.addPh")} aria-label={t("prod.entry.addFor", { k: label })}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} onBlur={() => { if (val.trim()) add(); }}
          className="w-20 rounded border border-slate-300 px-2 py-1 text-right font-mono text-sm" />
        <button onMouseDown={(e) => e.preventDefault()} onClick={add} className="rounded bg-slate-900 px-2.5 py-1 text-xs font-semibold text-white">{t("prod.entry.addBtn")}</button>
      </span>
    );
  }
  return (
    <span className="ml-auto flex items-center gap-1">
      <button onClick={() => onDelta(-1, true)} disabled={qty <= 0} className="h-7 w-7 rounded border border-slate-300 disabled:opacity-40">−</button>
      <input value={total} onChange={(e) => setTotal(e.target.value)} inputMode="numeric" aria-label={t("prod.entry.totalFor", { k: label })}
        onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
        onBlur={() => { const v = Number(total.replace(",", ".")); if (total.trim() === "" || !Number.isFinite(v) || v < 0) setTotal(String(qty)); else onSet(Math.min(99999, v)); }}
        className="w-16 rounded border border-slate-300 px-2 py-1 text-right font-mono text-sm" />
      <button onClick={() => onDelta(1, true)} className="h-7 w-7 rounded border border-slate-300">+</button>
    </span>
  );
}

// His `openConfirm`: a question over the page; Enter is yes, Escape or a click outside is no.
export function ConfirmModal({ question, detail, yes, down, onYes, onNo }: {
  question: string; detail?: string; yes: string; down?: boolean; onYes: () => void; onNo: () => void;
}) {
  const { t } = useT();
  const yesRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    yesRef.current?.focus();
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") { e.preventDefault(); onNo(); } };
    document.addEventListener("keydown", key, true);
    return () => document.removeEventListener("keydown", key, true);
  }, [onNo]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/40 p-4" onClick={(e) => { if (e.target === e.currentTarget) onNo(); }}>
      <div role="dialog" aria-modal="true" className={`w-full max-w-md rounded-xl bg-white p-5 shadow-xl ring-2 ${down ? "ring-amber-300" : "ring-emerald-300"}`}>
        <p className="text-base font-medium text-slate-900">{question}</p>
        {detail && <p className="mt-1 font-mono text-sm text-slate-600">{detail}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onNo} className="rounded-lg px-3 py-1.5 text-sm ring-1 ring-slate-300">{t("unit.no")}</button>
          <button ref={yesRef} onClick={onYes} className={`rounded-lg px-3 py-1.5 text-sm font-semibold text-white ${down ? "bg-amber-700" : "bg-emerald-700"}`}>{yes}</button>
        </div>
      </div>
    </div>
  );
}
